import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import companiesApi from "../../api/companiesApi";
import companyBranchesApi from "../../api/companyBranchesApi";
import usersApi from "../../api/usersApi";
import styles from "./UpdateCompanyPage.module.css";

function AreaEditor({ areas, onChange }) {
  const [value, setValue] = useState("");

  const addArea = () => {
    const area = value.trim();
    if (!area || areas.includes(area)) return;
    onChange([...areas, area]);
    setValue("");
  };

  return (
    <div>
      <div className={styles.areasInput}>
        <input
          className={styles.input}
          value={value}
          placeholder="Nueva área"
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              addArea();
            }
          }}
        />
        <button type="button" className={styles.addButton} onClick={addArea}>Agregar</button>
      </div>
      <div className={styles.chipList}>
        {areas.map((area) => (
          <span key={area} className={styles.chip}>
            {area}
            <button type="button" onClick={() => onChange(areas.filter((item) => item !== area))}>×</button>
          </span>
        ))}
      </div>
    </div>
  );
}

function BranchForm({ companyId, branch, existingAdmins, onSaved, onCancel }) {
  const [name, setName] = useState(branch?.name || "");
  const [jobAreas, setJobAreas] = useState(branch?.jobAreas || []);
  const [adminInput, setAdminInput] = useState("");
  const [pendingAdmins, setPendingAdmins] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const addAdmin = () => {
    const username = adminInput.trim().toLowerCase();
    if (!username || pendingAdmins.includes(username)) return;
    setPendingAdmins((current) => [...current, username]);
    setAdminInput("");
  };

  const save = async () => {
    if (!name.trim()) {
      setError("El nombre de la sucursal es obligatorio.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload = { name: name.trim(), jobAreas };
      const savedBranch = branch?._id
        ? await companyBranchesApi.update(branch._id, payload)
        : await companyBranchesApi.create({ ...payload, companyId });

      const passwords = [];
      const failedAdmins = [];
      for (const username of pendingAdmins) {
        try {
          const created = await usersApi.createUser({
            username,
            role: "admin",
            company_id: savedBranch._id,
          });
          passwords.push({ username, password: created.generatedPassword });
        } catch (adminError) {
          console.error("Error vinculando administrador:", adminError);
          failedAdmins.push(username);
        }
      }

      onSaved(savedBranch, passwords, failedAdmins);
    } catch (requestError) {
      console.error("Error guardando sucursal:", requestError);
      setError(requestError.response?.data?.message || "No se pudo guardar la sucursal.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.branchEditor}>
      <h3>{branch?._id ? `Editar ${branch.name}` : "Nueva sucursal"}</h3>
      <div className={styles.section}>
        <label className={styles.label}>Nombre de la sucursal</label>
        <input className={styles.input} value={name} onChange={(event) => setName(event.target.value)} autoFocus />
      </div>
      <div className={styles.section}>
        <label className={styles.label}>Áreas de trabajo</label>
        <AreaEditor areas={jobAreas} onChange={setJobAreas} />
      </div>
      <div className={styles.section}>
        <label className={styles.label}>Administradores (opcional)</label>
        <p className={styles.uploadHint}>Los administradores nuevos quedarán vinculados directamente a esta sucursal.</p>
        {existingAdmins.length > 0 && (
          <div className={styles.chipList}>
            {existingAdmins.map((admin) => <span className={styles.chip} key={admin._id}>{admin.username}</span>)}
          </div>
        )}
        <div className={styles.areasInput}>
          <input
            className={styles.input}
            value={adminInput}
            placeholder="correo@empresa.com"
            onChange={(event) => setAdminInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addAdmin();
              }
            }}
          />
          <button type="button" className={styles.addButton} onClick={addAdmin}>Agregar</button>
        </div>
        <div className={styles.chipList}>
          {pendingAdmins.map((username) => (
            <span key={username} className={styles.chipPending}>
              {username}
              <button type="button" onClick={() => setPendingAdmins((items) => items.filter((item) => item !== username))}>×</button>
            </span>
          ))}
        </div>
      </div>
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.buttonRow}>
        <button type="button" className={styles.cancelBranchButton} onClick={onCancel}>Cancelar</button>
        <button type="button" className={styles.submitButton} disabled={saving} onClick={save}>
          {saving ? "Guardando..." : "Guardar sucursal"}
        </button>
      </div>
    </div>
  );
}

export default function UpdateCompanyPage() {
  const { idCompany } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [editingBranch, setEditingBranch] = useState(null);
  const [showBranchForm, setShowBranchForm] = useState(false);
  const [companyEditing, setCompanyEditing] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyLogo, setCompanyLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finishing, setFinishing] = useState(false);
  const [passwords, setPasswords] = useState([]);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [companyData, branchData, usersData] = await Promise.all([
        companiesApi.getCompanyById(idCompany),
        companyBranchesApi.getByCompany(idCompany),
        usersApi.getUsersByCompany(idCompany),
      ]);
      setCompany(companyData);
      setCompanyName(companyData.name);
      setBranches(branchData);
      setUsers(usersData.users || []);
    } catch (error) {
      console.error("Error cargando configuración:", error);
      setMessage("No se pudo cargar la configuración de la empresa.");
    } finally {
      setLoading(false);
    }
  }, [idCompany]);

  useEffect(() => { load(); }, [load]);

  const adminsByBranch = useMemo(() => {
    const result = {};
    branches.forEach((branch) => { result[String(branch._id)] = []; });
    users.filter((user) => user.role === "admin").forEach((user) => {
      (user.branches || []).forEach((branchId) => {
        if (result[String(branchId)]) result[String(branchId)].push(user);
      });
    });
    return result;
  }, [branches, users]);

  const handleBranchSaved = async (_branch, createdPasswords, failedAdmins) => {
    setShowBranchForm(false);
    setEditingBranch(null);
    if (createdPasswords.length) setPasswords(createdPasswords);
    if (failedAdmins.length) {
      setMessage(`La sucursal se guardó, pero no fue posible crear: ${failedAdmins.join(", ")}.`);
    } else {
      setMessage("Sucursal guardada correctamente.");
    }
    await load();
  };

  const saveCompanyBasics = async () => {
    if (!companyName.trim()) return;
    const formData = new FormData();
    formData.append("name", companyName.trim());
    if (companyLogo) formData.append("logo", companyLogo, companyLogo.name);
    try {
      await companiesApi.updateCompany(idCompany, formData);
      setCompanyEditing(false);
      setCompanyLogo(null);
      await load();
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudieron actualizar los datos de la empresa.");
    }
  };

  const deleteBranch = async (branch) => {
    if (!window.confirm(`¿Eliminar la sucursal ${branch.name}?`)) return;
    await companyBranchesApi.delete(branch._id);
    await load();
  };

  const finish = async () => {
    if (!branches.length) {
      setMessage("Debes agregar al menos una sucursal antes de finalizar.");
      return;
    }
    setFinishing(true);
    try {
      await companiesApi.completeConfiguration(idCompany);
      navigate("/empresas");
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudo finalizar la configuración.");
    } finally {
      setFinishing(false);
    }
  };

  if (loading) return <div className={styles.container}>Cargando configuración...</div>;

  return (
    <div className={styles.container}>
      <div className={styles.steps}>
        <span className={styles.stepDone}>1. Empresa</span>
        <span className={styles.stepActive}>2. Sucursales</span>
        <span>3. Configuración opcional</span>
      </div>

      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.title}>{location.state?.fromCreation ? "Configura las sucursales" : "Sucursales"}</h2>
          <p className={styles.pageLead}>{company?.name}</p>
        </div>
        <button type="button" className={styles.secondaryButton} onClick={() => setCompanyEditing((value) => !value)}>
          Editar empresa
        </button>
      </div>

      {companyEditing && (
        <div className={styles.companySection}>
          <div className={styles.section}>
            <label className={styles.label}>Nombre</label>
            <input className={styles.input} value={companyName} onChange={(event) => setCompanyName(event.target.value)} />
          </div>
          <div className={styles.section}>
            <label className={styles.label}>Nuevo logo (opcional)</label>
            <input type="file" accept="image/*" onChange={(event) => setCompanyLogo(event.target.files[0] || null)} />
          </div>
          <div className={styles.buttonRow}>
            <button type="button" className={styles.submitButton} onClick={saveCompanyBasics}>Guardar empresa</button>
          </div>
        </div>
      )}

      <div className={styles.requirementNotice}>
        Debes agregar al menos una sucursal. Los cuestionarios y archivos se configuran después y son opcionales.
      </div>
      {message && <div className={styles.message}>{message}</div>}

      <section className={styles.branchesSection}>
        <div className={styles.sectionHeadingRow}>
          <h3 className={styles.sectionTitle}>Sucursales ({branches.length})</h3>
          <button
            type="button"
            className={styles.addBranchButton}
            onClick={() => { setEditingBranch(null); setShowBranchForm(true); }}
          >
            + Agregar sucursal
          </button>
        </div>

        {showBranchForm && (
          <BranchForm
            companyId={idCompany}
            branch={editingBranch}
            existingAdmins={editingBranch ? (adminsByBranch[String(editingBranch._id)] || []) : []}
            onSaved={handleBranchSaved}
            onCancel={() => { setShowBranchForm(false); setEditingBranch(null); }}
          />
        )}

        {!branches.length && !showBranchForm && (
          <div className={styles.emptyState}>Todavía no hay sucursales. Agrega la primera para continuar.</div>
        )}

        <div className={styles.branchList}>
          {branches.map((branch) => {
            const admins = adminsByBranch[String(branch._id)] || [];
            const applicationsCount = (branch.surveys || []).reduce(
              (total, survey) => total + (survey.applications?.length || 0),
              0
            );
            return (
              <article className={styles.branchSummary} key={branch._id}>
                <div className={styles.branchSummaryMain}>
                  <h4>{branch.name}</h4>
                  <p>{branch.jobAreas?.length || 0} áreas · {admins.length} administradores · {applicationsCount} aplicaciones</p>
                  <div className={styles.chipList}>
                    {(branch.jobAreas || []).map((area) => <span className={styles.chip} key={area}>{area}</span>)}
                  </div>
                </div>
                <div className={styles.branchActions}>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => { setEditingBranch(branch); setShowBranchForm(true); }}
                  >
                    Editar datos
                  </button>
                  <button
                    type="button"
                    className={styles.configureButton}
                    onClick={() => navigate(`/company/${idCompany}/branches/${branch._id}/configure`)}
                  >
                    Configurar
                  </button>
                  <button type="button" className={styles.removeButton} onClick={() => deleteBranch(branch)}>Eliminar</button>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <div className={styles.footerActions}>
        <button type="button" className={styles.secondaryButton} onClick={() => navigate("/empresas")}>Terminar después</button>
        <button type="button" className={styles.finishButton} disabled={!branches.length || finishing} onClick={finish}>
          {finishing ? "Finalizando..." : "Finalizar configuración"}
        </button>
      </div>

      {passwords.length > 0 && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Administradores creados</h3>
            <p>Guarda estas contraseñas; se solicitará cambiarlas en el primer acceso.</p>
            <table className={styles.areasTable}>
              <tbody>{passwords.map((item) => <tr key={item.username}><td>{item.username}</td><td>{item.password}</td></tr>)}</tbody>
            </table>
            <button type="button" className={styles.finishButton} onClick={() => setPasswords([])}>Entendido</button>
          </div>
        </div>
      )}
    </div>
  );
}
